import siteMeta from '../config/siteMeta';
import projectCatalogModule from '../content/projectCatalog';

const {
  projectCatalog,
  buildProjectSeoEntry,
  findProjectByPath,
  getProjectPath,
} = projectCatalogModule;

const siteStructure = siteMeta.buildSiteStructure();

export const SITE_ORIGIN = siteMeta.SITE_ORIGIN;
export const SITE_NAME = siteMeta.SITE_NAME;
export const SITE_ALTERNATE_NAME = siteMeta.SITE_ALTERNATE_NAME;
export const DEFAULT_OG_IMAGE = siteMeta.DEFAULT_OG_IMAGE;
export const DEFAULT_DESCRIPTION = siteMeta.PRIMARY_SEO_DESCRIPTION;
export const DEFAULT_OG_IMAGE_ALT = siteMeta.DEFAULT_OG_IMAGE_ALT;
export const HOMEPAGE_PREVIEW_TEXT = siteMeta.HOMEPAGE_PREVIEW_TEXT;
export const ROBOTS_CONTENT = siteMeta.ROBOTS_CONTENT;
export const SOCIAL_LINKS = siteMeta.SOCIAL_LINKS;
export const TWITTER_HANDLE = siteMeta.TWITTER_HANDLE;

const STATIC_ROUTE_SEO = Object.fromEntries(
  siteStructure.topLevelPages.map((page) => [
    page.path,
    {
      title: page.title,
      description: page.seoDescription || page.description,
      previewDescription: page.description,
      pageType: page.path === '/' ? 'WebSite' : 'WebPage',
      keywords: [
        SITE_NAME,
        'FIRST Robotics Competition',
        'FRC Istanbul',
        page.title.replace(` | ${SITE_NAME}`, ''),
      ],
    },
  ])
);

const PROJECT_ROUTE_SEO = Object.fromEntries(
  projectCatalog.map((project) => {
    const entry = buildProjectSeoEntry(project);
    return [
      entry.path,
      {
        ...entry,
        project,
        pageType: 'CreativeWork',
        keywords: [
          project.title,
          'INTEGRA 3646 projects',
          'FIRST Robotics Competition outreach',
          'STEM education',
        ],
      },
    ];
  })
);

const NAVIGATION_LABELS = Object.fromEntries(
  siteStructure.primaryNavigation.map((item) => [item.path, item.label])
);

export const INDEXABLE_ROUTES = [
  ...siteStructure.topLevelPages.map((page) => page.path),
  ...projectCatalog.map((project) => getProjectPath(project)),
];

export function normalizePathname(pathname) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const trimmed = pathname.trim();
  if (!trimmed.startsWith('/')) {
    return `/${trimmed}`;
  }

  return trimmed.replace(/\/+$/, '') || '/';
}

function toAbsoluteUrl(routePath) {
  return routePath === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${routePath}`;
}

function getPageLabel(pathname, seo, project) {
  if (project) {
    return project.title;
  }

  if (NAVIGATION_LABELS[pathname]) {
    return NAVIGATION_LABELS[pathname];
  }

  if (pathname === '/privacy') {
    return 'Privacy';
  }

  return seo.title.replace(` | ${SITE_NAME}`, '');
}

function buildBreadcrumbItems(pathname, seo, project) {
  const items = [
    {
      name: 'Home',
      url: toAbsoluteUrl('/'),
    },
  ];

  if (pathname === '/') {
    return items;
  }

  if (project) {
    items.push({
      name: 'Projects',
      url: toAbsoluteUrl('/projects'),
    });
  }

  items.push({
    name: getPageLabel(pathname, seo, project),
    url: seo.canonicalUrl,
  });

  return items;
}

function buildBreadcrumbStructuredData(items) {
  if (items.length <= 1) {
    return null;
  }

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function buildOrganizationStructuredData() {
  return {
    '@type': ['Organization', 'EducationalOrganization'],
    '@id': `${toAbsoluteUrl('/')}#organization`,
    name: SITE_NAME,
    alternateName: SITE_ALTERNATE_NAME,
    url: toAbsoluteUrl('/'),
    logo: DEFAULT_OG_IMAGE,
    description: DEFAULT_DESCRIPTION,
    sameAs: SOCIAL_LINKS.map((link) => link.href),
  };
}

function buildWebsiteStructuredData() {
  return {
    '@type': 'WebSite',
    '@id': `${toAbsoluteUrl('/')}#website`,
    name: SITE_NAME,
    alternateName: SITE_ALTERNATE_NAME,
    url: toAbsoluteUrl('/'),
    description: DEFAULT_DESCRIPTION,
    publisher: {
      '@id': `${toAbsoluteUrl('/')}#organization`,
    },
  };
}

function buildProjectsCollectionStructuredData(seo) {
  return [
    {
      '@type': 'CollectionPage',
      name: seo.title,
      description: seo.description,
      url: seo.canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: toAbsoluteUrl('/'),
      },
      about: [
        'STEM outreach',
        'FIRST Robotics Competition',
        'community impact',
      ],
    },
    {
      '@type': 'ItemList',
      name: 'INTEGRA 3646 Projects',
      itemListOrder: 'https://schema.org/ItemListUnordered',
      numberOfItems: projectCatalog.length,
      itemListElement: projectCatalog.map((project, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: project.title,
        url: toAbsoluteUrl(getProjectPath(project)),
      })),
    },
  ];
}

function buildProjectStructuredData(seo, project) {
  return {
    '@type': 'CreativeWork',
    name: project.title,
    headline: project.title,
    description: seo.description,
    url: seo.canonicalUrl,
    image: seo.ogImage,
    creator: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: toAbsoluteUrl('/'),
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
    isPartOf: {
      '@type': 'CollectionPage',
      name: 'Projects',
      url: toAbsoluteUrl('/projects'),
    },
    about: [
      project.title,
      'FIRST Robotics Competition',
      'STEM outreach',
      'INTEGRA 3646',
    ],
  };
}

function buildWebPageStructuredData(seo) {
  return {
    '@type': seo.pageType,
    name: seo.title,
    description: seo.description,
    url: seo.canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: toAbsoluteUrl('/'),
    },
    image: seo.ogImage,
  };
}

function buildStructuredData(pathname, seo, project) {
  const breadcrumbItems = buildBreadcrumbItems(pathname, seo, project);
  const breadcrumbSchema = buildBreadcrumbStructuredData(breadcrumbItems);

  const graph = [
    buildOrganizationStructuredData(),
    buildWebsiteStructuredData(),
  ];
  if (pathname === '/projects') {
    graph.push(...buildProjectsCollectionStructuredData(seo));
  } else if (project) {
    graph.push(buildProjectStructuredData(seo, project));
  } else {
    graph.push(buildWebPageStructuredData(seo));
  }

  if (breadcrumbSchema) {
    graph.push(breadcrumbSchema);
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

function finalizeSeo(pathname, seo, project = null) {
  const canonicalPath = project ? getProjectPath(project) : pathname;

  return {
    ...seo,
    indexable: true,
    canonicalUrl: toAbsoluteUrl(canonicalPath),
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: project ? `${project.title} project overview for ${SITE_NAME}` : `${SITE_NAME} logo`,
    structuredData: buildStructuredData(pathname, {
      ...seo,
      canonicalUrl: toAbsoluteUrl(canonicalPath),
      ogImage: DEFAULT_OG_IMAGE,
    }, project),
  };
}

export function getSeoForPath(pathname) {
  const normalizedPath = normalizePathname(pathname);
  const project = findProjectByPath(normalizedPath);

  if (project) {
    return finalizeSeo(normalizedPath, PROJECT_ROUTE_SEO[getProjectPath(project)], project);
  }

  const routeSeo = STATIC_ROUTE_SEO[normalizedPath];
  if (routeSeo) {
    return finalizeSeo(normalizedPath, routeSeo);
  }

  return {
    title: `Page Not Found | ${SITE_NAME}`,
    description: DEFAULT_DESCRIPTION,
    indexable: false,
    canonicalUrl: toAbsoluteUrl('/'),
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: `${SITE_NAME} logo`,
    structuredData: null,
  };
}
